// src/pages/InventoryOrderView.js
// 叫貨檢視（後台 InventoryAdmin 與班別老闆前台共用）

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import socket from '../lib/socket';
import InventoryOrderAlerts from '../components/InventoryOrderAlerts';

const api = (url, opts) => fetch(url, { headers: { 'Content-Type': 'application/json' }, ...opts }).then(r => r.json());

const WD = ['日', '一', '二', '三', '四', '五', '六'];
const OPERATOR_KEY = 'inv-order-operator';
/** 叫貨明細桌面欄：商品 + 點貨／安全／常備／最大／建議 */
const ORDER_COLS = 'minmax(160px, 1.6fr) 64px 56px 56px 56px 72px';
const ORDER_MIN_W = 560;

function round1(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.round((x + Number.EPSILON) * 10) / 10;
}

function formatQty(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '0';
  return String(round1(x)).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}

function formatYmdLabel(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd || '';
  const [y, m, d] = ymd.split('-').map(Number);
  const wd = new Date(y, m - 1, d).getDay();
  return `${m}/${d}(${WD[wd]})`;
}

function formatCheckedAt(iso) {
  if (!iso) return '';
  try {
    const dt = new Date(iso);
    const m = dt.getMonth() + 1;
    const d = dt.getDate();
    const hh = String(dt.getHours()).padStart(2, '0');
    const mm = String(dt.getMinutes()).padStart(2, '0');
    return `${m}/${d} ${hh}:${mm}`;
  } catch { return ''; }
}

/** 通用複選下拉：value 為已選 value 陣列；空陣列表示不篩選（顯示全部） */
function MultiSelect({ options, value, onChange, disabled, compact, emptyLabel = '全部', selectedLabel }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = (v) => {
    if (value.includes(v)) onChange(value.filter(x => x !== v));
    else onChange([...value, v]);
  };

  const label = value.length === 0
    ? emptyLabel
    : (selectedLabel ? selectedLabel(value) : `已選 ${value.length} 項`);

  const btnStyle = {
    padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 15, fontWeight: 600,
    background: disabled ? '#f1f5f9' : '#fff', color: disabled ? '#94a3b8' : '#1e293b', cursor: disabled ? 'not-allowed' : 'pointer',
    minWidth: compact ? '100%' : 160, width: compact ? '100%' : undefined,
    textAlign: 'left', display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: compact ? '100%' : undefined, flex: compact ? '1 1 100%' : undefined }}>
      <button type="button" disabled={disabled || options.length === 0} onClick={() => !disabled && options.length > 0 && setOpen(o => !o)} style={btnStyle}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <span style={{ fontSize: 12, opacity: 0.7 }}>▾</span>
      </button>
      {open && options.length > 0 && (
        <div style={{
          position: 'absolute', zIndex: 100, left: 0, top: 'calc(100% + 4px)',
          minWidth: compact ? '100%' : 260, maxWidth: compact ? '100%' : 360, width: compact ? '100%' : undefined,
          maxHeight: 300,
          overflowY: 'auto', background: '#fff', border: '1px solid #d1d5db', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)', padding: '8px 10px',
        }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => onChange(options.map(o => o.value))}
              style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #cbd5e1', background: '#f8fafc', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              全選
            </button>
            <button type="button" onClick={() => onChange([])}
              style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #cbd5e1', background: '#f8fafc', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              清除
            </button>
          </div>
          {options.map(o => (
            <label
              key={o.value}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '6px 4px', cursor: 'pointer', fontSize: 15,
                borderRadius: 4,
              }}
            >
              <input
                type="checkbox"
                checked={value.includes(o.value)}
                onChange={() => toggle(o.value)}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
              <span style={{ flex: 1 }}>{o.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default function InventoryOrderView({ compact = false }) {
  const [orderDays, setOrderDays] = useState([]);
  const [orderDate, setOrderDate] = useState('');
  const [selectedDates, setSelectedDates] = useState([]); // 手動覆寫；空＝用預設
  const [defaultDates, setDefaultDates] = useState([]);
  const [candidateDates, setCandidateDates] = useState([]);
  const [data, setData] = useState([]);
  const [vendorsMeta, setVendorsMeta] = useState([]);
  const [orderChecks, setOrderChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState({});
  const [filterVendors, setFilterVendors] = useState([]);
  const [operator, setOperator] = useState(() => {
    try { return localStorage.getItem(OPERATOR_KEY) || ''; } catch { return ''; }
  });
  const [checkBusy, setCheckBusy] = useState({});

  const loadOrderDays = useCallback(async () => {
    try {
      const res = await api('/api/inventory/order-days');
      if (res.success) {
        const list = res.list || [];
        setOrderDays(list);
        return list;
      }
    } catch (e) { console.error(e); }
    return [];
  }, []);

  useEffect(() => {
    loadOrderDays().then(list => {
      if (list.length > 0) {
        // 預設選今天或最近的叫貨日
        const today = (() => {
          const d = new Date();
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        })();
        const hit = list.find(x => x.order_date === today) || list[0];
        setOrderDate(hit.order_date);
        setSelectedDates([]);
        setDefaultDates(hit.default_dates || []);
        setCandidateDates(hit.candidate_dates || []);
      } else {
        setLoading(false);
      }
    });
  }, [loadOrderDays]);

  const load = useCallback(async (od, datesOverride) => {
    if (!od) return;
    setLoading(true);
    try {
      let url = `/api/inventory/order-view?date=${od}`;
      if (Array.isArray(datesOverride) && datesOverride.length > 0) {
        url += `&dates=${datesOverride.join(',')}`;
      }
      const res = await api(url);
      if (res.success) {
        setData(res.items || []);
        setVendorsMeta(res.vendors || []);
        setOrderChecks(res.order_checks || []);
        if (res.default_dates) setDefaultDates(res.default_dates);
        if (res.source_dates && (!datesOverride || datesOverride.length === 0)) {
          // 後端實際採用的預設
        }
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!orderDate) return;
    const od = orderDays.find(d => d.order_date === orderDate);
    if (od) {
      setDefaultDates(od.default_dates || []);
      setCandidateDates(od.candidate_dates || []);
    }
    setSelectedDates([]);
    setFilterVendors([]);
    load(orderDate, null);
  }, [orderDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // 手動覆寫點貨日後重載
  useEffect(() => {
    if (!orderDate) return;
    if (selectedDates.length === 0) return; // 空＝預設，由上面的 effect 處理
    load(orderDate, selectedDates);
  }, [selectedDates]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onRecord = () => {
      loadOrderDays();
      if (orderDate) load(orderDate, selectedDates.length > 0 ? selectedDates : null);
    };
    const onCheck = () => {
      if (orderDate) load(orderDate, selectedDates.length > 0 ? selectedDates : null);
    };
    socket.on('inventory:record', onRecord);
    socket.on('inventory:order-check', onCheck);
    socket.on('inventory:vendors', onCheck);
    return () => {
      socket.off('inventory:record', onRecord);
      socket.off('inventory:order-check', onCheck);
      socket.off('inventory:vendors', onCheck);
    };
  }, [orderDate, selectedDates, load, loadOrderDays]);

  const onOperatorChange = (v) => {
    setOperator(v);
    try { localStorage.setItem(OPERATOR_KEY, v); } catch { /* noop */ }
  };

  const activeSourceDates = selectedDates.length > 0 ? selectedDates : defaultDates;
  const isDefaultSelection = selectedDates.length === 0
    || (selectedDates.length === defaultDates.length
      && selectedDates.every(d => defaultDates.includes(d)));

  const vendorOptions = useMemo(() => {
    const set = new Set();
    data.forEach(item => set.add(item.vendor || '未指定廠商'));
    return [...set].sort((a, b) => a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [data]);

  const dateOptions = useMemo(() => {
    const set = new Set([...(candidateDates || []), ...(defaultDates || []), ...selectedDates]);
    return [...set].sort().reverse().map(d => ({ value: d, label: formatYmdLabel(d) }));
  }, [candidateDates, defaultDates, selectedDates]);

  const checkMap = useMemo(() => {
    const m = {};
    (orderChecks || []).forEach(c => { m[c.vendor_id] = c; });
    return m;
  }, [orderChecks]);

  const vendorMetaById = useMemo(() => {
    const m = {};
    (vendorsMeta || []).forEach(v => { m[v.id] = v; });
    return m;
  }, [vendorsMeta]);

  const grouped = useMemo(() => {
    const map = {};
    data.forEach(item => {
      const vendor = item.vendor || '未指定廠商';
      if (!map[vendor]) map[vendor] = { vendor, vendor_id: item.vendor_id, items: [] };
      // 保留第一個有 vendor_id 的
      if (map[vendor].vendor_id == null && item.vendor_id != null) {
        map[vendor].vendor_id = item.vendor_id;
      }
      map[vendor].items.push(item);
    });
    return Object.values(map).sort((a, b) => a.vendor.localeCompare(b.vendor));
  }, [data]);

  const groupedFiltered = useMemo(() => {
    if (filterVendors.length === 0) return grouped;
    const sel = new Set(filterVendors);
    return grouped.filter(g => sel.has(g.vendor));
  }, [grouped, filterVendors]);

  const orderBaseOf = (item) => (item.standing_stock != null ? item.standing_stock : item.max_stock);
  const suggestOf = (item) => {
    if (item.quantity == null) return null;
    const base = orderBaseOf(item);
    if (base == null) return null;
    return round1(Math.max(0, base - item.quantity));
  };

  const isBelowSafety = (item) => {
    const hasRecord = item.quantity != null && item.quantity !== '';
    const safetyNum = Number(item.safety_line) || 0;
    return hasRecord && safetyNum > 0 && Number(item.quantity) <= safetyNum;
  };

  const displayName = (item) => {
    const vin = String(item.vendor_item_name || '').trim();
    return vin || item.name;
  };

  const generateText = (vendor, vendorItems) => {
    const lines = vendorItems
      .filter(isBelowSafety)
      .map((item) => {
        const n = suggestOf(item);
        const qtyPart = n != null && n > 0 ? ` ${formatQty(n)}` : ' ';
        return `${displayName(item)}${qtyPart}${item.unit || ''}`;
      });
    if (!lines.length) return '';
    return `【${vendor}】\n${lines.join('\n')}`;
  };

  const copyText = async (vendor, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(p => ({ ...p, [vendor]: true }));
      setTimeout(() => setCopied(p => ({ ...p, [vendor]: false })), 2000);
    } catch { alert('複製失敗，請手動複製'); }
  };

  const toggleOrdered = async (vendorId, nextChecked) => {
    if (vendorId == null) {
      alert('此廠商尚未建立主檔，請先到「廠商設定」建立');
      return;
    }
    const name = String(operator || '').trim();
    if (nextChecked && !name) {
      alert('請先填寫操作者姓名');
      return;
    }
    setCheckBusy(p => ({ ...p, [vendorId]: true }));
    try {
      const res = await api('/api/inventory/order-check', {
        method: 'POST',
        body: JSON.stringify({
          order_date: orderDate,
          vendor_id: vendorId,
          checked: nextChecked,
          operator: name,
        }),
      });
      if (res.success) {
        await load(orderDate, selectedDates.length > 0 ? selectedDates : null);
      } else {
        alert(res.error || '更新失敗');
      }
    } catch {
      alert('更新失敗');
    } finally {
      setCheckBusy(p => ({ ...p, [vendorId]: false }));
    }
  };

  // 未達低消（前端即時算）
  const belowMin = useMemo(() => {
    const out = [];
    for (const g of grouped) {
      if (g.vendor_id == null) continue;
      const meta = vendorMetaById[g.vendor_id];
      if (!meta || meta.min_order_amount == null) continue;
      let amount = 0;
      let missing = false;
      let anyNeed = false;
      for (const item of g.items) {
        if (!isBelowSafety(item)) continue;
        const n = suggestOf(item);
        anyNeed = true;
        const useQty = n != null && n > 0 ? n : 0;
        if (item.unit_price == null || item.unit_price === '') {
          missing = true;
          continue;
        }
        amount += useQty * Number(item.unit_price);
      }
      if (!anyNeed) continue;
      amount = Math.round((amount + Number.EPSILON) * 100) / 100;
      if (amount < Number(meta.min_order_amount)) {
        out.push({
          vendor_id: g.vendor_id,
          vendor: g.vendor,
          amount,
          min_order_amount: Number(meta.min_order_amount),
          missing_price: missing,
        });
      }
    }
    return out;
  }, [grouped, vendorMetaById]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <InventoryOrderAlerts operator={operator} belowMin={belowMin} />

      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end',
        marginBottom: 14,
        padding: compact ? 0 : '12px 14px',
        background: compact ? 'transparent' : '#f8fafc',
        border: compact ? 'none' : '1px solid #e5e7eb',
        borderRadius: 10,
      }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 1 150px', minWidth: compact ? '100%' : 130 }}>
          <span style={{ fontWeight: 700, fontSize: 12, color: '#64748b' }}>叫貨日</span>
          {orderDays.length === 0 ? (
            <span style={{ fontSize: 14, color: '#94a3b8', padding: '6px 0' }}>尚無可叫貨日期</span>
          ) : (
            <select value={orderDate} onChange={e => setOrderDate(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 15, fontWeight: 600, background: '#fff', width: '100%' }}>
              {orderDays.map(d => (
                <option key={d.order_date} value={d.order_date}>{formatYmdLabel(d.order_date)}</option>
              ))}
            </select>
          )}
        </label>

        {orderDate && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 240px', minWidth: compact ? '100%' : 200 }}>
            <span style={{ fontWeight: 700, fontSize: 12, color: '#64748b' }}>點貨日</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <MultiSelect
                  options={dateOptions}
                  value={selectedDates.length > 0 ? selectedDates : defaultDates}
                  onChange={(vals) => {
                    const sorted = [...vals].sort();
                    const defSorted = [...defaultDates].sort();
                    if (sorted.length === defSorted.length && sorted.every((d, i) => d === defSorted[i])) {
                      setSelectedDates([]);
                      load(orderDate, null);
                    } else {
                      setSelectedDates(vals);
                    }
                  }}
                  disabled={loading || dateOptions.length === 0}
                  compact
                  emptyLabel="（無）"
                  selectedLabel={(vals) => vals.map(formatYmdLabel).join('＋') || '（無）'}
                />
              </div>
              {!isDefaultSelection && (
                <button
                  type="button"
                  onClick={() => { setSelectedDates([]); load(orderDate, null); }}
                  style={{
                    padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e1',
                    background: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', color: '#475569',
                    whiteSpace: 'nowrap', flexShrink: 0,
                  }}
                >
                  預設
                </button>
              )}
            </div>
          </div>
        )}

        {orderDate && !loading && grouped.length > 0 && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 180px', minWidth: compact ? '100%' : 160 }}>
            <span style={{ fontWeight: 700, fontSize: 12, color: '#64748b' }}>廠商</span>
            <MultiSelect
              options={vendorOptions}
              value={filterVendors}
              onChange={setFilterVendors}
              disabled={loading}
              compact
              emptyLabel="全部廠商"
              selectedLabel={(vals) => `已選 ${vals.length} 個廠商`}
            />
          </label>
        )}

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 1 120px', minWidth: compact ? '100%' : 100 }}>
          <span style={{ fontWeight: 700, fontSize: 12, color: '#64748b' }}>操作者</span>
          <input
            value={operator}
            onChange={e => onOperatorChange(e.target.value)}
            placeholder="姓名"
            style={{
              padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db',
              fontSize: 15, fontWeight: 600, width: '100%', boxSizing: 'border-box',
            }}
          />
        </label>
      </div>

      {orderDate && activeSourceDates.length > 0 && (
        <div style={{
          marginBottom: 12, padding: '8px 12px', borderRadius: 8,
          background: '#f1f5f9', color: '#334155', fontSize: 14, fontWeight: 600,
        }}>
          本頁統整：{activeSourceDates.map(formatYmdLabel).join('＋')}
          {!isDefaultSelection && <span style={{ marginLeft: 8, color: '#b45309', fontWeight: 700 }}>（已手動調整）</span>}
        </div>
      )}

      {loading ? (
        <p style={{ color: '#64748b', padding: 24, textAlign: 'center', fontSize: 16 }}>載入中…</p>
      ) : !orderDate || grouped.length === 0 ? (
        <p style={{ color: '#94a3b8', textAlign: 'center', padding: 24, fontSize: 16 }}>該日期尚無記錄</p>
      ) : groupedFiltered.length === 0 ? (
        <p style={{ color: '#94a3b8', textAlign: 'center', padding: 24, fontSize: 16 }}>沒有符合廠商篩選的項目</p>
      ) : (
        groupedFiltered.map((g) => {
          const { vendor, vendor_id, items: vendorItems } = g;
          const needOrderItems = vendorItems.filter(isBelowSafety);
          const orderText = generateText(vendor, vendorItems);
          const chk = vendor_id != null ? checkMap[vendor_id] : null;
          const meta = vendor_id != null ? vendorMetaById[vendor_id] : null;
          return (
            <div key={`${vendor}-${vendor_id || 'x'}`} style={{ marginBottom: 16 }}>
              <div style={{
                padding: '10px 14px', background: '#1e293b', color: '#fff',
                borderRadius: '10px 10px 0 0', fontSize: 17, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span>{vendor}</span>
                  {meta?.min_order_amount != null && (
                    <span style={{ fontSize: 12, fontWeight: 600, opacity: 0.85 }}>
                      低消 {Number(meta.min_order_amount).toLocaleString('zh-TW')}
                    </span>
                  )}
                  <label style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontSize: 14, fontWeight: 600, cursor: vendor_id == null ? 'not-allowed' : 'pointer',
                    opacity: vendor_id == null ? 0.5 : 1,
                  }}>
                    <input
                      type="checkbox"
                      checked={!!chk}
                      disabled={vendor_id == null || !!checkBusy[vendor_id]}
                      onChange={(e) => toggleOrdered(vendor_id, e.target.checked)}
                      style={{ width: 16, height: 16 }}
                    />
                    已叫貨
                  </label>
                  {chk && (
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#86efac' }}>
                      ✓ {formatCheckedAt(chk.checked_at)} {chk.checked_by}
                    </span>
                  )}
                </div>
                {needOrderItems.length > 0 && (
                  <span style={{ fontSize: 14, background: '#ef4444', padding: '2px 10px', borderRadius: 999 }}>
                    {needOrderItems.length} 項需叫貨
                  </span>
                )}
              </div>
              <div style={{
                background: '#fff', border: '1px solid #e5e7eb', borderTop: 'none',
                borderRadius: '0 0 10px 10px', overflowX: compact ? 'visible' : 'auto',
                WebkitOverflowScrolling: 'touch',
              }}>
                {!compact && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: ORDER_COLS,
                    gap: 8,
                    alignItems: 'center',
                    padding: '8px 12px',
                    background: '#f1f5f9',
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#64748b',
                    position: 'sticky',
                    top: 52,
                    zIndex: 5,
                    minWidth: ORDER_MIN_W,
                  }}>
                    <span style={{ position: 'sticky', left: 0, background: '#f1f5f9', paddingRight: 8, boxShadow: '2px 0 0 #e5e7eb' }}>商品</span>
                    <span style={{ textAlign: 'right' }}>點貨</span>
                    <span style={{ textAlign: 'right' }}>安全</span>
                    <span style={{ textAlign: 'right' }}>常備</span>
                    <span style={{ textAlign: 'right' }}>最大</span>
                    <span style={{ textAlign: 'right' }}>建議</span>
                  </div>
                )}
                {vendorItems.map((item, idx) => {
                  const hasRecord = item.quantity != null && item.quantity !== '';
                  const qtyNum = hasRecord ? Number(item.quantity) : null;
                  const safetyNum = Number(item.safety_line) || 0;
                  const belowSafety = hasRecord && safetyNum > 0 && qtyNum <= safetyNum;
                  const noRecord = !hasRecord;
                  const need = suggestOf(item);
                  const toOrder = belowSafety;
                  const needLabel = hasRecord && need != null ? formatQty(need) : '—';
                  const rowBg = noRecord ? '#fffbeb' : toOrder ? '#fef2f2' : (idx % 2 === 0 ? '#fff' : '#f8fafc');
                  const vin = String(item.vendor_item_name || '').trim();
                  if (compact) {
                    return (
                      <div key={item.item_id} style={{
                        padding: '12px 14px', borderTop: '1px solid #f1f5f9', background: rowBg,
                      }}>
                        <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 16, marginBottom: 4 }}>
                          {item.name}
                          {belowSafety && <span style={{ color: '#ef4444', fontSize: 13, marginLeft: 6 }}>⚠ 低於安全線</span>}
                          {noRecord && <span style={{
                            marginLeft: 6, fontSize: 11, fontWeight: 700,
                            background: '#f59e0b', color: '#fff', padding: '1px 6px', borderRadius: 4,
                          }}>未點貨</span>}
                        </div>
                        {vin && vin !== item.name && (
                          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 6 }}>廠商：{vin}</div>
                        )}
                        <div style={{
                          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', fontSize: 14,
                          color: '#334155',
                        }}>
                          <div>點貨量：<strong style={{ color: '#0f172a' }}>{hasRecord ? formatQty(item.quantity) : '—'}</strong></div>
                          <div>安全線：<strong style={{ color: '#2563eb' }}>{formatQty(item.safety_line)}</strong></div>
                          <div>常備庫存：<strong style={{ color: '#0f766e' }}>{item.standing_stock == null ? '—' : formatQty(item.standing_stock)}</strong></div>
                          <div>最大庫存：<strong style={{ color: '#7c3aed' }}>{item.max_stock == null ? '—' : formatQty(item.max_stock)}</strong></div>
                          <div style={{ gridColumn: '1 / -1' }}>建議叫貨：<strong style={{ color: toOrder ? '#dc2626' : '#16a34a' }}>{needLabel}</strong></div>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={item.item_id} style={{
                      display: 'grid',
                      gridTemplateColumns: ORDER_COLS,
                      gap: 8,
                      alignItems: 'center',
                      padding: '10px 12px',
                      borderTop: '1px solid #f1f5f9',
                      background: rowBg,
                      minWidth: ORDER_MIN_W,
                    }}>
                      <span style={{
                        fontWeight: 600, color: '#1e293b', fontSize: 15, minWidth: 0,
                        position: 'sticky', left: 0, background: rowBg, paddingRight: 8,
                        boxShadow: '2px 0 0 #e5e7eb',
                      }}>
                        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.name}>
                          {item.name}
                        </span>
                        {vin && vin !== item.name && (
                          <span style={{ display: 'block', fontSize: 11, fontWeight: 500, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {vin}
                          </span>
                        )}
                        {belowSafety && <span style={{ color: '#ef4444', fontSize: 11 }}>⚠ 低於安全線</span>}
                        {noRecord && <span style={{
                          marginLeft: belowSafety ? 4 : 0, fontSize: 10, fontWeight: 700,
                          background: '#f59e0b', color: '#fff', padding: '1px 5px', borderRadius: 4,
                        }}>未點貨</span>}
                      </span>
                      <span style={{ fontSize: 15, color: hasRecord ? '#1e293b' : '#f59e0b', fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {hasRecord ? formatQty(item.quantity) : '—'}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#2563eb', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {formatQty(item.safety_line)}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#0f766e', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {item.standing_stock == null ? '—' : formatQty(item.standing_stock)}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#7c3aed', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {item.max_stock == null ? '—' : formatQty(item.max_stock)}
                      </span>
                      <span style={{
                        fontSize: 15, fontWeight: 800, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                        color: toOrder ? '#dc2626' : '#16a34a',
                      }}>
                        {needLabel}
                      </span>
                    </div>
                  );
                })}

                {orderText && (
                  <div style={{ padding: '12px 14px', borderTop: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: '#dc2626' }}>叫貨訊息：</span>
                      <button type="button" onClick={() => copyText(vendor, orderText)}
                        style={{
                          padding: '4px 14px', borderRadius: 6, border: '1px solid #d1d5db',
                          background: copied[vendor] ? '#dcfce7' : '#fff',
                          color: copied[vendor] ? '#16a34a' : '#374151',
                          fontSize: 14, fontWeight: 700, cursor: 'pointer', transition: 'all .2s',
                        }}>
                        {copied[vendor] ? '已複製 ✓' : '複製文字'}
                      </button>
                    </div>
                    <pre style={{
                      margin: 0, padding: '10px 12px', background: '#f8fafc', borderRadius: 6,
                      fontSize: 15, lineHeight: 1.7, color: '#1e293b', whiteSpace: 'pre-wrap',
                      border: '1px solid #e2e8f0',
                    }}>
                      {orderText}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
